import { queryOptions, useQuery } from '@tanstack/react-query';

import { TimeRange, ApiResponse, QueryOptions } from '@cast/shared-types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const apiFn = apiClient.dbo.getDbInstance;

export type DbInstanceDetailsQueryData = ApiResponse<typeof apiFn>;

export type DbInstanceDetailsQueryArgs<SELECT> = QueryOptions<
  DbInstanceDetailsQueryData,
  SELECT
> & {
  instanceId?: string;
  key: string;
  getRange: () => TimeRange;
};

const DBO_DB_INSTANCE_QUERY_KEY = 'dbo-db-instance';

export const getDbInstanceDetailsQueryKey = ({
  key,
  instanceId,
}: { key?: string; instanceId?: string } = {}) =>
  [DBO_DB_INSTANCE_QUERY_KEY, key, instanceId] as const;

const getDbInstanceDetailsQueryOptions = ({
  instanceId,
  key,
  getRange,
  enabled,
}: {
  instanceId?: string;
  key: string;
  getRange: () => TimeRange;
  enabled?: boolean;
}) =>
  queryOptions({
    queryKey: getDbInstanceDetailsQueryKey({ key, instanceId }),
    queryFn: async () => {
      const { from, to } = getRange();
      return apiFn({
        instanceId: instanceId!,
        startTime: from,
        endTime: to,
      }).then(({ data }) => data);
    },
    enabled: makeIsEnabled(!!instanceId, enabled),
  });

export const useDbInstanceDetailsQuery = <SELECT = DbInstanceDetailsQueryData>({
  instanceId,
  key,
  getRange,
  enabled,
  select,
}: DbInstanceDetailsQueryArgs<SELECT>) => {
  return useQuery({
    ...getDbInstanceDetailsQueryOptions({
      instanceId,
      key,
      getRange,
      enabled,
    }),
    select,
  });
};
